import { Component } from 'react';
import PropTypes from 'prop-types';
import { Link, Navigate } from 'react-router-dom';
import { Card, Row, Col, Form, Button, Table, Badge, Modal, Spinner, Alert, ProgressBar } from 'react-bootstrap';
import { FaEye, FaPause, FaPlay, FaSearch, FaSyncAlt, FaUserSecret } from 'react-icons/fa';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { withTranslation } from 'react-i18next';
import { AppSelect, AppAsyncSelect } from '../../common/_components/AppSelect';
import Paginacao from '../../common/pagination';
import { getCurrentUser } from '../../common/utils';
import { withRouter } from '../../common/with-router';
import anonimizacaoService from '../../api/suporte/anonimizacaoService';
import privacidadeService, { REGRAS_ANONIMIZACAO } from '../../api/programas/privacidadeService';
import programasService from '../../api/programas/programasService';
import { formatarDataHora, formatarNumero } from '../../i18n/format';

const MySwal = withReactContent(Swal);

const TAMANHO_LOTE = 50;
const LIMITE_PAGINA = 30;

const STATUS_EXECUCAO_BADGE = {
    previa: 'secondary',
    em_andamento: 'info',
    concluida: 'success',
    concluida_com_erros: 'warning',
};

const STATUS_ITEM = ['pendente', 'sucesso', 'ignorado', 'erro'];

const STATUS_ITEM_BADGE = {
    pendente: 'secondary',
    sucesso: 'success',
    ignorado: 'warning',
    erro: 'danger',
};

const ehSuperuser = () => !!getCurrentUser()?.is_superuser;

/** Mensagem de erro por código de negócio, com o `detail` do backend como fallback. */
const mensagemDeErro = (t, error, fallback) => {
    const dados = error?.response?.data;

    if (dados?.codigo) {
        return t(`privacidade:errors.${dados.codigo}`, { defaultValue: dados.detail || fallback });
    }

    if (dados?.detail) {
        return dados.detail;
    }

    if (error?.response?.status === 403) {
        return t('privacidade:errors.forbidden');
    }

    return fallback;
};

class AnonimizacaoRegras extends Component {
    constructor(props) {
        super(props);

        this.montado = false;
        this.pausaSolicitada = false;

        this.state = {
            programa: null,

            carregandoConfig: false,
            erroConfig: false,
            config: null,

            gerandoPrevia: false,
            previa: null,

            execucao: null,
            executando: false,
            pausando: false,
            iniciandoRetomada: false,

            carregandoHistorico: false,
            erroHistorico: false,
            historico: {},
            pageHistorico: 1,

            showDetalhe: false,
            detalheExecucaoId: null,
            carregandoDetalhe: false,
            erroDetalhe: false,
            detalhe: {},
            pageDetalhe: 1,
            filtroStatusDetalhe: '',
        };
    }

    componentDidMount() {
        this.montado = true;

        if (!ehSuperuser()) {
            return;
        }

        const programaId = new URLSearchParams(this.props.router.location.search).get('programa_id');

        if (programaId) {
            this.carregarProgramaDaUrl(programaId);
        }
    }

    componentWillUnmount() {
        this.montado = false;
        this.pausaSolicitada = true;
    }

    carregarProgramaDaUrl = async (programaId) => {
        try {
            const { data } = await privacidadeService.getPrograma(programaId);
            this.selecionarPrograma({ value: data.id, label: data.nome });
        } catch {
            this.selecionarPrograma({ value: Number(programaId) || programaId, label: String(programaId) });
        }
    };

    selecionarPrograma = (opcao) => {
        if (!this.montado) {
            return;
        }

        this.props.router.navigate(
            { search: opcao ? `?programa_id=${opcao.value}` : '' },
            { replace: true }
        );

        this.setState({
            programa: opcao || null,
            config: null,
            erroConfig: false,
            previa: null,
            execucao: null,
            historico: {},
            pageHistorico: 1,
            erroHistorico: false,
        }, () => {
            if (opcao) {
                this.carregarConfig();
                this.carregarHistorico(1);
            }
        });
    };

    carregarConfig = async () => {
        const { programa } = this.state;

        this.setState({ carregandoConfig: true, erroConfig: false });

        try {
            const { data } = await privacidadeService.get(programa.value);

            if (this.montado) {
                this.setState({ carregandoConfig: false, config: data });
            }
        } catch {
            if (this.montado) {
                this.setState({ carregandoConfig: false, erroConfig: true });
            }
        }
    };

    carregarHistorico = async (page = 1) => {
        const { programa } = this.state;

        if (!programa) {
            return;
        }

        this.setState({ carregandoHistorico: true, erroHistorico: false });

        try {
            const { data } = await anonimizacaoService.listarExecucoes({ programa_id: programa.value, page });

            if (this.montado) {
                this.setState({ carregandoHistorico: false, historico: data, pageHistorico: page });
            }
        } catch {
            if (this.montado) {
                this.setState({ carregandoHistorico: false, erroHistorico: true });
            }
        }
    };

    gerarPrevia = async () => {
        const { t } = this.props;
        const { programa } = this.state;

        this.setState({ gerandoPrevia: true, previa: null, execucao: null });

        try {
            const { data } = await anonimizacaoService.previa(programa.value, true);

            if (this.montado) {
                this.setState({ gerandoPrevia: false, previa: data });
                this.carregarHistorico(1);
            }
        } catch (error) {
            if (!this.montado) {
                return;
            }

            this.setState({ gerandoPrevia: false });

            const dados = error?.response?.data;

            if (dados?.codigo === 'execucao_em_aberto' && dados?.status_execucao === 'em_andamento' && dados?.execucao_id) {
                const resposta = await MySwal.fire({
                    icon: 'question',
                    title: t('privacidade:execution.openExecution.title'),
                    text: t('privacidade:execution.openExecution.text'),
                    showCancelButton: true,
                    confirmButtonText: t('privacidade:execution.progress.resume'),
                    cancelButtonText: t('common:button.cancel'),
                });

                if (resposta.isConfirmed) {
                    this.retomarExecucao(dados.execucao_id);
                }
                return;
            }

            MySwal.fire({
                icon: 'error',
                title: t('common:alert.error'),
                text: mensagemDeErro(t, error, t('privacidade:errors.preview')),
            });
        }
    };

    /** Pede a confirmação forte. Retorna o nome confirmado ou null se cancelado. */
    confirmarAnonimizacao = async (execucao) => {
        const { t } = this.props;
        const nomePrograma = execucao?.programa_nome || this.state.programa?.label || '';
        const quantidade = execucao?.restantes ?? execucao?.total_beneficiarios ?? 0;

        const resposta = await MySwal.fire({
            icon: 'warning',
            title: t('privacidade:execution.confirm.title'),
            html: (
                <div className="text-start">
                    <p>{t('privacidade:execution.confirm.summary', { count: quantidade })}</p>
                    <p className="text-danger fw-bold">{t('privacidade:execution.confirm.irreversible')}</p>
                    <p className="mb-1">{t('privacidade:execution.confirm.typeName')}</p>
                    <code className="user-select-all">{nomePrograma}</code>
                </div>
            ),
            input: 'text',
            inputPlaceholder: t('privacidade:execution.confirm.inputPlaceholder'),
            inputAttributes: { autocomplete: 'off', autocapitalize: 'off' },
            showCancelButton: true,
            focusCancel: false,
            confirmButtonColor: '#dc3545',
            confirmButtonText: t('privacidade:execution.confirm.confirmButton'),
            cancelButtonText: t('common:button.cancel'),
            inputValidator: (valor) => (
                (valor || '').trim() !== nomePrograma.trim()
                    ? t('privacidade:execution.confirm.nameMismatch')
                    : undefined
            ),
        });

        return resposta.isConfirmed ? nomePrograma : null;
    };

    anonimizar = async () => {
        const { previa } = this.state;

        if (!previa || !previa.total_beneficiarios) {
            return;
        }

        const confirmacao = await this.confirmarAnonimizacao(previa);

        if (confirmacao !== null) {
            this.executarEmLoop(previa.id, confirmacao);
        }
    };

    retomarExecucao = async (execucaoId) => {
        const { t } = this.props;

        this.setState({ iniciandoRetomada: true });

        let execucao;

        try {
            const { data } = await anonimizacaoService.detalheExecucao(execucaoId, { page: 1 });
            execucao = data.execucao;
        } catch (error) {
            if (this.montado) {
                this.setState({ iniciandoRetomada: false });
                MySwal.fire({
                    icon: 'error',
                    title: t('common:alert.error'),
                    text: mensagemDeErro(t, error, t('privacidade:execution.detail.loadError')),
                });
            }
            return;
        }

        if (!this.montado) {
            return;
        }

        this.setState({ iniciandoRetomada: false, execucao, previa: null });

        const confirmacao = await this.confirmarAnonimizacao(execucao);

        if (confirmacao !== null) {
            this.executarEmLoop(execucao.id, confirmacao);
        }
    };

    executarEmLoop = async (execucaoId, confirmacao) => {
        const { t } = this.props;

        this.pausaSolicitada = false;
        this.setState({ executando: true, pausando: false });

        let execucao = null;
        let semProgresso = false;

        try {
            do {
                const processadosAntes = execucao?.processados ?? this.state.execucao?.processados ?? -1;
                const { data } = await anonimizacaoService.executar(execucaoId, confirmacao, TAMANHO_LOTE);
                execucao = data;

                if (!this.montado) {
                    return;
                }

                this.setState({ execucao: data });

                // Proteção contra loop infinito caso o backend não avance.
                if (data.restantes > 0 && data.processados <= processadosAntes) {
                    semProgresso = true;
                    break;
                }
            } while (execucao.restantes > 0 && !this.pausaSolicitada);
        } catch (error) {
            if (this.montado) {
                const codigo = error?.response?.data?.codigo;
                const previaInvalida = ['previa_expirada', 'regras_alteradas', 'status_invalido'].includes(codigo);

                this.setState((prev) => ({
                    executando: false,
                    pausando: false,
                    previa: previaInvalida ? null : prev.previa,
                }));

                MySwal.fire({
                    icon: 'error',
                    title: t('common:alert.error'),
                    text: mensagemDeErro(t, error, t('privacidade:errors.execute')),
                });
                this.carregarHistorico(1);
            }
            return;
        }

        this.setState({ executando: false, pausando: false });
        this.carregarHistorico(1);

        if (semProgresso) {
            MySwal.fire({ icon: 'error', title: t('common:alert.error'), text: t('privacidade:errors.noProgress') });
            return;
        }

        if (execucao.restantes === 0) {
            this.setState({ previa: null });

            const comErros = execucao.status === 'concluida_com_erros' || execucao.erros > 0;

            MySwal.fire({
                icon: comErros ? 'warning' : 'success',
                title: comErros
                    ? t('privacidade:execution.result.titleWithErrors')
                    : t('privacidade:execution.result.title'),
                text: t('privacidade:execution.result.text', {
                    sucesso: formatarNumero(execucao.sucesso),
                    ignorados: formatarNumero(execucao.ignorados),
                    erros: formatarNumero(execucao.erros),
                }),
            });
        }
    };

    pausar = () => {
        this.pausaSolicitada = true;
        this.setState({ pausando: true });
    };

    abrirDetalhe = (execucao) => {
        this.setState({
            showDetalhe: true,
            detalheExecucaoId: execucao.id,
            detalhe: {},
            pageDetalhe: 1,
            filtroStatusDetalhe: '',
            erroDetalhe: false,
        }, () => this.carregarDetalhe(1));
    };

    carregarDetalhe = async (page = 1) => {
        const { detalheExecucaoId, filtroStatusDetalhe } = this.state;
        const params = { page };

        if (filtroStatusDetalhe) {
            params.status = filtroStatusDetalhe;
        }

        this.setState({ carregandoDetalhe: true, erroDetalhe: false });

        try {
            const { data } = await anonimizacaoService.detalheExecucao(detalheExecucaoId, params);

            if (this.montado) {
                this.setState({ carregandoDetalhe: false, detalhe: data, pageDetalhe: page });
            }
        } catch {
            if (this.montado) {
                this.setState({ carregandoDetalhe: false, erroDetalhe: true });
            }
        }
    };

    renderStatusExecucao = (status) => {
        const { t } = this.props;

        return (
            <Badge bg={STATUS_EXECUCAO_BADGE[status] || 'secondary'}>
                {t(`privacidade:status.${status}`, { defaultValue: status })}
            </Badge>
        );
    };

    renderRegrasAtendidas = (regras) => {
        const { t } = this.props;

        return (regras || []).map((regra) => (
            <Badge key={regra} bg="light" text="dark" className="me-1 border">
                {t(`privacidade:ruleCode.${regra}`, { defaultValue: regra })}
            </Badge>
        ));
    };

    renderSelecaoPrograma() {
        const { t } = this.props;
        const { programa, executando, gerandoPrevia, iniciandoRetomada } = this.state;

        return (
            <Card className="cardDestaque mb-3">
                <Card.Body>
                    <Row>
                        <Col xs={12}>
                            <p className="text-muted">{t('privacidade:execution.intro')}</p>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6} xs={12}>
                            <Form.Group>
                                <Form.Label>{t('privacidade:execution.program')}</Form.Label>
                                <AppAsyncSelect
                                    loadOptions={programasService.loadProgramas}
                                    placeholder={t('privacidade:execution.programPlaceholder')}
                                    value={programa}
                                    isDisabled={executando || gerandoPrevia || iniciandoRetomada}
                                    onChange={this.selecionarPrograma}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    }

    renderRegras() {
        const { t } = this.props;
        const { programa, config, carregandoConfig, erroConfig, previa, gerandoPrevia, executando, iniciandoRetomada } = this.state;

        if (carregandoConfig) {
            return (
                <Card className="cardDestaque mb-3">
                    <Card.Body className="text-center py-4"><Spinner /></Card.Body>
                </Card>
            );
        }

        if (erroConfig) {
            return <Alert variant="danger">{t('privacidade:execution.rules.loadError')}</Alert>;
        }

        if (!config) {
            return null;
        }

        const algumaAtiva = REGRAS_ANONIMIZACAO.some((regra) => config[`anonimizar_${regra}_dias`] != null);

        return (
            <Card className="cardDestaque mb-3">
                <Card.Body>
                    <h5>{t('privacidade:execution.rules.title')}</h5>
                    <p className="text-muted small">{t('privacidade:execution.rules.businessRule')}</p>

                    <Table size="sm" hover className="mb-3">
                        <tbody>
                            {REGRAS_ANONIMIZACAO.map((regra) => {
                                const dias = config[`anonimizar_${regra}_dias`];

                                return (
                                    <tr key={regra}>
                                        <td className="fw-bold">{t(`privacidade:ruleCode.${regra}`)}</td>
                                        <td>
                                            {dias != null
                                                ? t('privacidade:execution.rules.days', { count: dias })
                                                : <span className="text-muted">{t('privacidade:execution.rules.off')}</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                            <tr>
                                <td className="fw-bold">{t('privacidade:execution.rules.pharmacovigilance')}</td>
                                <td>
                                    {config.anonimizar_farmacovigilancia
                                        ? t('privacidade:execution.rules.pvIncluded')
                                        : t('privacidade:execution.rules.pvExcluded')}
                                </td>
                            </tr>
                        </tbody>
                    </Table>

                    {!algumaAtiva && (
                        <Alert variant="warning">
                            <div>{t('privacidade:execution.rules.noneActive')}</div>
                            <Link to={`/programas/edit/${programa.value}`}>
                                {t('privacidade:execution.rules.configure')}
                            </Link>
                        </Alert>
                    )}

                    <Button
                        variant="secondary"
                        onClick={this.gerarPrevia}
                        disabled={!algumaAtiva || gerandoPrevia || executando || iniciandoRetomada}
                    >
                        {gerandoPrevia ? <Spinner size="sm" className="me-1" /> : <FaSearch className="me-1" />}
                        {t('privacidade:execution.preview.generate')}
                    </Button>

                    {previa && !executando && (
                        <Button
                            variant="danger"
                            className="ms-2"
                            onClick={this.anonimizar}
                            disabled={!previa.total_beneficiarios || gerandoPrevia || iniciandoRetomada}
                        >
                            <FaUserSecret className="me-1" /> {t('privacidade:execution.anonymize')}
                        </Button>
                    )}
                </Card.Body>
            </Card>
        );
    }

    renderPrevia() {
        const { t } = this.props;
        const { previa } = this.state;

        if (!previa) {
            return null;
        }

        const porRegra = Object.entries(previa.resumo?.por_regra || {});
        const ignoradosPorMotivo = Object.entries(previa.resumo?.ignorados || {});
        const amostra = previa.amostra || [];

        return (
            <Card className="cardDestaque mb-3">
                <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">{t('privacidade:execution.preview.title')}</h5>
                        {previa.expira_em && (
                            <small className="text-muted">
                                {t('privacidade:execution.preview.validUntil', { data: formatarDataHora(previa.expira_em) })}
                            </small>
                        )}
                    </div>

                    <Row className="mb-3">
                        <Col md={4} xs={12} className="mb-2">
                            <div className="border rounded p-3 text-center h-100">
                                <div className="text-muted small">{t('privacidade:execution.preview.patients')}</div>
                                <div className="fs-3 fw-bold">{formatarNumero(previa.total_beneficiarios)}</div>
                            </div>
                        </Col>
                        <Col md={4} xs={12} className="mb-2">
                            <div className="border rounded p-3 text-center h-100">
                                <div className="text-muted small">{t('privacidade:execution.preview.encounters')}</div>
                                <div className="fs-3 fw-bold">{formatarNumero(previa.total_atendimentos)}</div>
                            </div>
                        </Col>
                        <Col md={4} xs={12} className="mb-2">
                            <div className="border rounded p-3 text-center h-100">
                                <div className="text-muted small">{t('privacidade:execution.preview.ignored')}</div>
                                <div className="fs-3 fw-bold">{formatarNumero(previa.total_ignorados)}</div>
                            </div>
                        </Col>
                    </Row>

                    {!previa.total_beneficiarios && (
                        <Alert variant="info">{t('privacidade:execution.preview.nothingToAnonymize')}</Alert>
                    )}

                    <Row className="mb-3">
                        <Col md={6} xs={12} className="mb-2">
                            <h6>{t('privacidade:execution.preview.byRule')}</h6>
                            {porRegra.length ? (
                                <Table size="sm" hover className="mb-1 w-auto">
                                    <tbody>
                                        {porRegra.map(([regra, total]) => (
                                            <tr key={regra}>
                                                <td>{t(`privacidade:ruleCode.${regra}`, { defaultValue: regra })}</td>
                                                <td className="text-end ps-4">{formatarNumero(total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <p className="text-muted small">{t('privacidade:execution.preview.none')}</p>
                            )}
                            <Form.Text>{t('privacidade:execution.preview.byRuleHelp')}</Form.Text>
                        </Col>
                        <Col md={6} xs={12} className="mb-2">
                            <h6>{t('privacidade:execution.preview.ignoredByReason')}</h6>
                            {ignoradosPorMotivo.length ? (
                                <Table size="sm" hover className="w-auto">
                                    <tbody>
                                        {ignoradosPorMotivo.map(([motivo, total]) => (
                                            <tr key={motivo}>
                                                <td>{t(`privacidade:reason.${motivo}`, { defaultValue: motivo })}</td>
                                                <td className="text-end ps-4">{formatarNumero(total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <p className="text-muted small">{t('privacidade:execution.preview.none')}</p>
                            )}
                        </Col>
                    </Row>

                    <h6>{t('privacidade:execution.preview.sample')}</h6>
                    <Table size="sm" hover responsive className="custom-table">
                        <thead>
                            <tr>
                                <th>{t('privacidade:execution.preview.patientId')}</th>
                                <th>{t('privacidade:execution.preview.initials')}</th>
                                <th className="text-center">{t('privacidade:execution.preview.encountersCount')}</th>
                                <th>{t('privacidade:execution.preview.rulesMatched')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {amostra.length ? amostra.map((item) => (
                                <tr key={item.beneficiario_id}>
                                    <td>{item.beneficiario_id}</td>
                                    <td>{item.iniciais}</td>
                                    <td className="text-center">{formatarNumero(item.qtd_atendimentos)}</td>
                                    <td>{this.renderRegrasAtendidas(item.regras_atendidas)}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={4} className="text-center text-muted">
                                        {t('privacidade:execution.preview.sampleEmpty')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        );
    }

    renderProgresso() {
        const { t } = this.props;
        const { execucao, executando, pausando, gerandoPrevia, iniciandoRetomada } = this.state;

        if (!execucao || execucao.status === 'previa') {
            return null;
        }

        const total = execucao.total_beneficiarios || 0;
        const processados = execucao.processados || 0;
        const percentual = total ? Math.min(100, Math.round((processados / total) * 100)) : 100;
        const pausada = !executando && execucao.status === 'em_andamento' && execucao.restantes > 0;

        return (
            <Card className="cardDestaque mb-3">
                <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">
                            {t('privacidade:execution.progress.title', { id: execucao.id })}{' '}
                            {this.renderStatusExecucao(execucao.status)}
                        </h5>
                        <div>
                            {executando && (
                                <Button variant="outline-secondary" size="sm" onClick={this.pausar} disabled={pausando}>
                                    <FaPause className="me-1" /> {t('privacidade:execution.progress.pause')}
                                </Button>
                            )}
                            {pausada && (
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => this.retomarExecucao(execucao.id)}
                                    disabled={gerandoPrevia || iniciandoRetomada}
                                >
                                    <FaPlay className="me-1" /> {t('privacidade:execution.progress.resume')}
                                </Button>
                            )}
                        </div>
                    </div>

                    <ProgressBar
                        now={percentual}
                        label={`${percentual}%`}
                        animated={executando}
                        striped={executando}
                        variant={execucao.erros > 0 ? 'warning' : 'success'}
                        className="mb-2"
                    />
                    <div className="small text-muted mb-3">
                        {t('privacidade:execution.progress.processed', {
                            processados: formatarNumero(processados),
                            total: formatarNumero(total),
                        })}
                    </div>

                    <Row className="text-center">
                        <Col xs={6} md={3} className="mb-2">
                            <div className="text-muted small">{t('privacidade:execution.progress.success')}</div>
                            <div className="fs-5 fw-bold text-success">{formatarNumero(execucao.sucesso)}</div>
                        </Col>
                        <Col xs={6} md={3} className="mb-2">
                            <div className="text-muted small">{t('privacidade:execution.progress.ignored')}</div>
                            <div className="fs-5 fw-bold text-warning">{formatarNumero(execucao.ignorados)}</div>
                        </Col>
                        <Col xs={6} md={3} className="mb-2">
                            <div className="text-muted small">{t('privacidade:execution.progress.errors')}</div>
                            <div className="fs-5 fw-bold text-danger">{formatarNumero(execucao.erros)}</div>
                        </Col>
                        <Col xs={6} md={3} className="mb-2">
                            <div className="text-muted small">{t('privacidade:execution.progress.remaining')}</div>
                            <div className="fs-5 fw-bold">{formatarNumero(execucao.restantes)}</div>
                        </Col>
                    </Row>

                    {executando && !pausando && (
                        <Alert variant="info" className="mb-0 small">{t('privacidade:execution.progress.running')}</Alert>
                    )}
                    {executando && pausando && (
                        <Alert variant="secondary" className="mb-0 small">{t('privacidade:execution.progress.pausing')}</Alert>
                    )}
                    {pausada && (
                        <Alert variant="warning" className="mb-0 small">{t('privacidade:execution.progress.paused')}</Alert>
                    )}
                </Card.Body>
            </Card>
        );
    }

    renderHistorico() {
        const { t } = this.props;
        const { historico, carregandoHistorico, erroHistorico, pageHistorico, executando, gerandoPrevia, iniciandoRetomada } = this.state;
        const resultados = historico?.results || [];

        return (
            <Card className="cardDestaque mb-3">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">{t('privacidade:execution.history.title')}</h5>
                        <Button
                            variant="light"
                            size="sm"
                            onClick={() => this.carregarHistorico(pageHistorico)}
                            disabled={carregandoHistorico}
                        >
                            <FaSyncAlt className="me-1" /> {t('privacidade:execution.history.refresh')}
                        </Button>
                    </div>

                    {erroHistorico && <Alert variant="danger">{t('privacidade:execution.history.loadError')}</Alert>}

                    {!erroHistorico && (
                        <>
                            <Table size="sm" hover responsive className="custom-table">
                                <thead>
                                    <tr>
                                        <th>{t('privacidade:execution.history.id')}</th>
                                        <th>{t('privacidade:execution.history.status')}</th>
                                        <th>{t('privacidade:execution.history.createdAt')}</th>
                                        <th>{t('privacidade:execution.history.createdBy')}</th>
                                        <th>{t('privacidade:execution.history.confirmedBy')}</th>
                                        <th className="text-center">{t('privacidade:execution.history.patients')}</th>
                                        <th className="text-center">{t('privacidade:execution.progress.success')}</th>
                                        <th className="text-center">{t('privacidade:execution.progress.ignored')}</th>
                                        <th className="text-center">{t('privacidade:execution.progress.errors')}</th>
                                        <th>{t('privacidade:execution.history.finishedAt')}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {carregandoHistorico && (
                                        <tr><td colSpan={11} className="text-center py-3"><Spinner /></td></tr>
                                    )}
                                    {!carregandoHistorico && !resultados.length && (
                                        <tr>
                                            <td colSpan={11} className="text-center text-muted py-3">
                                                {t('privacidade:execution.history.empty')}
                                            </td>
                                        </tr>
                                    )}
                                    {!carregandoHistorico && resultados.map((execucao) => (
                                        <tr key={execucao.id}>
                                            <td>{execucao.id}</td>
                                            <td>{this.renderStatusExecucao(execucao.status)}</td>
                                            <td className="text-nowrap">{formatarDataHora(execucao.created_at)}</td>
                                            <td>{execucao.criado_por || '-'}</td>
                                            <td>{execucao.confirmado_por || '-'}</td>
                                            <td className="text-center">{formatarNumero(execucao.total_beneficiarios)}</td>
                                            <td className="text-center">{formatarNumero(execucao.sucesso)}</td>
                                            <td className="text-center">{formatarNumero(execucao.ignorados)}</td>
                                            <td className="text-center">{formatarNumero(execucao.erros)}</td>
                                            <td className="text-nowrap">{formatarDataHora(execucao.data_fim) || '-'}</td>
                                            <td className="text-nowrap text-end">
                                                <Button variant="light" size="sm" onClick={() => this.abrirDetalhe(execucao)}>
                                                    <FaEye className="me-1" /> {t('privacidade:execution.history.details')}
                                                </Button>
                                                {execucao.status === 'em_andamento' && (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        className="ms-1"
                                                        onClick={() => this.retomarExecucao(execucao.id)}
                                                        disabled={executando || gerandoPrevia || iniciandoRetomada}
                                                    >
                                                        <FaPlay className="me-1" /> {t('privacidade:execution.history.resume')}
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <Paginacao
                                dados={historico}
                                limit={LIMITE_PAGINA}
                                loading={carregandoHistorico}
                                page_numb={pageHistorico}
                                _onClickPagina={this.carregarHistorico}
                            />
                        </>
                    )}
                </Card.Body>
            </Card>
        );
    }

    renderModalDetalhe() {
        const { t } = this.props;
        const {
            showDetalhe, detalheExecucaoId, detalhe, carregandoDetalhe, erroDetalhe, pageDetalhe, filtroStatusDetalhe,
        } = this.state;
        const execucao = detalhe?.execucao;
        const itens = detalhe?.results || [];

        const opcoesStatus = [
            { value: '', label: t('privacidade:execution.detail.allStatuses') },
            ...STATUS_ITEM.map((status) => ({ value: status, label: t(`privacidade:itemStatus.${status}`) })),
        ];

        return (
            <Modal size="xl" show={showDetalhe} onHide={() => this.setState({ showDetalhe: false })}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('privacidade:execution.detail.title', { id: detalheExecucaoId })}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {execucao && (
                        <Row className="mb-3 small">
                            <Col md={3} xs={6} className="mb-2">
                                <strong>{t('privacidade:execution.history.status')}:</strong> {this.renderStatusExecucao(execucao.status)}
                            </Col>
                            <Col md={3} xs={6} className="mb-2">
                                <strong>{t('privacidade:execution.history.patients')}:</strong> {formatarNumero(execucao.total_beneficiarios)}
                            </Col>
                            <Col md={3} xs={6} className="mb-2">
                                <strong>{t('privacidade:execution.history.createdBy')}:</strong> {execucao.criado_por || '-'}
                            </Col>
                            <Col md={3} xs={6} className="mb-2">
                                <strong>{t('privacidade:execution.history.confirmedBy')}:</strong> {execucao.confirmado_por || '-'}
                            </Col>
                        </Row>
                    )}

                    <Row className="mb-3">
                        <Col md={4} xs={12}>
                            <Form.Group>
                                <Form.Label>{t('privacidade:execution.detail.filterStatus')}</Form.Label>
                                <AppSelect
                                    options={opcoesStatus}
                                    isClearable={false}
                                    value={opcoesStatus.find((o) => o.value === filtroStatusDetalhe) || opcoesStatus[0]}
                                    onChange={(opcao) => this.setState(
                                        { filtroStatusDetalhe: opcao?.value || '' },
                                        () => this.carregarDetalhe(1)
                                    )}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {erroDetalhe && <Alert variant="danger">{t('privacidade:execution.detail.loadError')}</Alert>}

                    {!erroDetalhe && (
                        <>
                            <Table size="sm" hover responsive className="custom-table">
                                <thead>
                                    <tr>
                                        <th>{t('privacidade:execution.preview.patientId')}</th>
                                        <th className="text-center">{t('privacidade:execution.preview.encountersCount')}</th>
                                        <th>{t('privacidade:execution.preview.rulesMatched')}</th>
                                        <th>{t('privacidade:execution.history.status')}</th>
                                        <th>{t('privacidade:execution.detail.reason')}</th>
                                        <th>{t('privacidade:execution.detail.error')}</th>
                                        <th>{t('privacidade:execution.detail.processedAt')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {carregandoDetalhe && (
                                        <tr><td colSpan={7} className="text-center py-3"><Spinner /></td></tr>
                                    )}
                                    {!carregandoDetalhe && !itens.length && (
                                        <tr>
                                            <td colSpan={7} className="text-center text-muted py-3">
                                                {t('privacidade:execution.detail.empty')}
                                            </td>
                                        </tr>
                                    )}
                                    {!carregandoDetalhe && itens.map((item) => {
                                        const detalhes = item.detalhes && typeof item.detalhes === 'object'
                                            ? JSON.stringify(item.detalhes)
                                            : item.detalhes;

                                        return (
                                            <tr key={item.id}>
                                                <td>{item.beneficiario_id}</td>
                                                <td className="text-center">{formatarNumero(item.qtd_atendimentos)}</td>
                                                <td>{this.renderRegrasAtendidas(item.regras_atendidas)}</td>
                                                <td>
                                                    <Badge bg={STATUS_ITEM_BADGE[item.status] || 'secondary'}>
                                                        {t(`privacidade:itemStatus.${item.status}`, { defaultValue: item.status })}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    {item.motivo
                                                        ? t(`privacidade:reason.${item.motivo}`, { defaultValue: item.motivo })
                                                        : '-'}
                                                </td>
                                                <td className="small" style={{ maxWidth: 280, wordBreak: 'break-word' }}>
                                                    {item.erro || detalhes || '-'}
                                                </td>
                                                <td className="text-nowrap">{formatarDataHora(item.data_processamento) || '-'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>

                            <Paginacao
                                dados={detalhe}
                                limit={LIMITE_PAGINA}
                                loading={carregandoDetalhe}
                                page_numb={pageDetalhe}
                                _onClickPagina={this.carregarDetalhe}
                            />
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-danger" onClick={() => this.setState({ showDetalhe: false })}>
                        {t('common:button.close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        const { t } = this.props;
        const { programa } = this.state;

        if (!ehSuperuser()) {
            return <Navigate to="/sem-permissao" />;
        }

        return (
            <div>
                <Row className="align-items-center mb-3">
                    <Col xs={12}>
                        <h4 className="page-title">{t('privacidade:execution.title')}</h4>
                    </Col>
                </Row>

                {this.renderSelecaoPrograma()}

                {!programa && (
                    <Alert variant="light" className="border">{t('privacidade:execution.selectProgram')}</Alert>
                )}

                {programa && (
                    <>
                        {this.renderRegras()}
                        {this.renderProgresso()}
                        {this.renderPrevia()}
                        {this.renderHistorico()}
                    </>
                )}

                {this.renderModalDetalhe()}
            </div>
        );
    }
}

AnonimizacaoRegras.propTypes = {
    t: PropTypes.func.isRequired,
    router: PropTypes.shape({
        location: PropTypes.object.isRequired,
        navigate: PropTypes.func.isRequired,
    }).isRequired,
};

const AnonimizacaoRegrasTraduzida = withTranslation(['privacidade', 'common'])(withRouter(AnonimizacaoRegras));

export default AnonimizacaoRegrasTraduzida;
